import { AbstractDestroyable } from '@deqo/foundation';
import { injectable } from 'inversify';
import { uuid } from '../CodesThatsGottaGoSomewhereElse';
import { resolve } from '../app/Container';
import { LocalStorageService, StorageService } from './StorageService';

export type AudioManagerMergeOpts = {
    id: string,
    playing: boolean,
    path: string,
    time: number,
    playlist?: string,
};

@injectable()
export class AudioManager extends AbstractDestroyable {

    @resolve(LocalStorageService) private storage!: StorageService;

    private tracks = new Map<string, HTMLAudioElement>();
    private preloads = new Map<string, HTMLAudioElement>();

    constructor() {
        super();
        this.burden(() => this.cleanup());
    }

    public get volume() {
        const val = parseFloat(this.storage.get('deqo/volume'));
        return Math.min(1, Math.max(0, !val && val !== 0 ? .4 : val));
    }

    public set volume(value: number) {
        this.storage.put('deqo/volume', value);
        this.tracks.forEach(x => x.volume = value);
    }

    public play(src: string): string;
    public play(id: string, src: string, startTime?: number): string;
    public play(...args: any[]) {
        const [id, src, startTime] = args.length > 1 ? args : [uuid(), args[0]];
        if (this.destroyed()) return;
        this.stop(id);
        const track = this.load(id, src);
        track.volume = this.volume;
        track.currentTime = startTime || 0;
        track.addEventListener('ended', () => this.stop(id));
        track.addEventListener('error', () => this.stop(id));
        try {
            track.play();
            this.tracks.set(id, track);
        }
        catch {
            // Ignore; usually because user hasn't clicked on the doc yet
        }
        return id;
    }

    public preload(id: string, src: string) {
        if (this.preloads.get(id)?.currentSrc === src) return;
        const track = new Audio(src);
        track.volume = 0;
        track.play();
        this.preloads.set(id, track);
    }

    public merge({ id, playing, path, time }: AudioManagerMergeOpts) {
        const track = this.tracks.get(id);
        if (!playing && !track) return;

        if (!playing) {
            this.stop(id);
        }
        else {
            if (track?.currentSrc !== path) {
                this.play(id, path, time);
            }
            else if (!(
                (track.currentTime <= time + 1) &&
                (track.currentTime >= time - 1)
            )) {
                // If track is out by > 2s, adjust
                track.currentTime = time;
                if (track.paused) {
                    track.play();
                }
            }
        }
    }

    public isPlaying(id: string) {
        if (this.destroyed()) return false;
        const track = this.tracks.get(id);
        if (!track) return false;
        return !track.paused;
    }

    public getTrackPath(id: string) {
        if (this.destroyed()) return null;
        const track = this.tracks.get(id);
        if (!track) return null;
        return track.currentSrc;
    }

    public pause(id: string) {
        if (this.destroyed()) return;
        const track = this.tracks.get(id);
        if (!track) return;
        track.pause();
    }

    public resume(id: string) {
        if (this.destroyed()) return false;
        const track = this.tracks.get(id);
        if (!track) return false;
        track.play();
        return true;
    }

    public currentTime(id: string) {
        if (this.destroyed()) return 0;
        const track = this.tracks.get(id);
        if (!track) return 0;
        return track.currentTime * 1000;
    }

    public stop(id: string) {
        if (this.destroyed()) return;
        const track = this.tracks.get(id);
        if (!track) return;
        track.pause();
        this.tracks.delete(id);
        this.preloads.delete(id);
    }

    private cleanup() {
        Array.from(this.tracks.keys()).forEach(x => this.stop(x));
        this.tracks.clear();
    }

    private load(id: string, src: string) {
        const preloaded = this.preloads.get(id);
        if (preloaded && preloaded.currentSrc === src) {
            return preloaded;
        }
        return new Audio(src);
    }
}
